import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, BadgeCheck } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { QuoteRibbon } from "@/components/site/RotatingQuote";
import { TestimonialSpotlight } from "@/components/site/TestimonialSpotlight";
import { ConversionBand } from "@/components/site/ConversionBand";
import { REVIEWS, REVIEW_CATEGORIES, type ReviewCategory } from "@/content/reviews";
import { Reveal } from "@/hooks/useReveal";
import { cn } from "@/lib/utils";

const aggregateRatingLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "DrSullySpeaks",
  url: "https://drsullyspeaks.com/",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: REVIEWS.length,
    bestRating: "5",
    worstRating: "1",
  },
  review: REVIEWS.slice(0, 10).map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 },
    reviewBody: r.quote,
  })),
};

const Reviews = () => {
  const [filter, setFilter] = useState<ReviewCategory | "all">("all");

  const visible = useMemo(
    () => (filter === "all" ? REVIEWS : REVIEWS.filter((r) => r.category === filter)),
    [filter],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Reviews — DrSullySpeaks · Executive Coach & Keynote Speaker"
        description="20 verified 5-star reviews of Dr. RD 'Sully' Sullivan — AI consultant, executive coach, keynote speaker, and CEO advisor. Voices from San Antonio to the globe."
        path="/reviews"
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aggregateRatingLd) }}
      />
      <SiteNav />

      <header className="relative bg-hero pt-32 md:pt-40 pb-16 md:pb-20 border-b border-border overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--gold)/0.08),transparent_50%)]"
        />
        <div className="container relative">
          <Reveal variant="up">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex" role="img" aria-label="5 out of 5 stars">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-gold text-gold" />
                ))}
              </div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-gold">
                5.0 · {REVIEWS.length} verified reviews
              </span>
            </div>
            <h1 className="font-display font-bold uppercase text-white text-5xl md:text-7xl leading-[0.95]">
              The proof is<br />
              <span className="text-gradient-gold">in the people.</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
              20 leaders — executives, founders, pastors, principals, council
              members, and chaplains — share what coaching with Dr. Sully
              unlocked. From San Antonio to Seoul, Lagos to Brooklyn.
            </p>
          </Reveal>

          <Reveal variant="up" delay={120}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquire?lane=coaching"
                className="group inline-flex items-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 shadow-cta hover:brightness-110 transition-all"
              >
                Become the next review
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/scan"
                className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-sm px-7 py-5 hover:bg-gold/10 hover:border-gold transition-all"
              >
                Start the MAKECHANGE Scan
              </Link>
            </div>
          </Reveal>
        </div>
      </header>

      {/* Filter chips */}
      <section className="py-10 border-b border-border bg-navy-deep sticky top-0 z-20 backdrop-blur-md">
        <div className="container">
          <div className="flex flex-wrap gap-2 justify-center">
            {REVIEW_CATEGORIES.map((c) => {
              const active = filter === c.value;
              const count =
                c.value === "all"
                  ? REVIEWS.length
                  : REVIEWS.filter((r) => r.category === c.value).length;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setFilter(c.value)}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.25em] border transition-all",
                    active
                      ? "bg-cta border-orange text-white shadow-cta"
                      : "bg-navy border-border text-silver hover:border-gold/40 hover:text-white",
                  )}
                >
                  {c.label}
                  <span className={cn("text-[9px]", active ? "text-white/80" : "text-gold/70")}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container">
          {visible.length === 0 ? (
            <p className="text-center text-silver">No reviews in this category yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {visible.map((r, i) => (
                <Reveal key={r.name} variant="up" delay={(i % 6) * 60}>
                  <figure
                    className="bg-navy border border-border p-6 md:p-7 h-full flex flex-col hover:border-gold/40 hover:-translate-y-1 transition-all"
                    itemScope
                    itemType="https://schema.org/Review"
                  >
                    <meta itemProp="reviewRating" content="5" />
                    <div className="flex items-center gap-4">
                      <img
                        src={r.image}
                        alt={`Portrait of ${r.name}`}
                        loading="lazy"
                        width={72}
                        height={72}
                        className="h-16 w-16 rounded-full object-cover border border-gold/30"
                      />
                      <div className="min-w-0 flex-1">
                        <div
                          className="font-display uppercase text-white text-base tracking-wide truncate"
                          itemProp="author"
                        >
                          {r.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-gold/80 truncate mt-0.5">
                          {r.location}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex gap-0.5" role="img" aria-label="5 out of 5 stars">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <Star key={idx} className="h-3.5 w-3.5 fill-gold text-gold" />
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] text-gold/90">
                        <BadgeCheck className="h-3 w-3" /> Verified Client
                      </span>
                    </div>
                    <blockquote
                      className="mt-4 text-silver text-sm md:text-base leading-relaxed"
                      itemProp="reviewBody"
                    >
                      "{r.quote}"
                    </blockquote>
                    <figcaption className="mt-5 pt-4 border-t border-border text-[11px] uppercase tracking-[0.22em] text-silver/80">
                      {r.role}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <TestimonialSpotlight />

      <ConversionBand
        eyebrow="Add Your Voice"
        heading="The next review could be yours."
        subheading="Start with the free Scan or apply for 1:1 coaching. Either way, you'll know within one business day."
        primaryLabel="Apply for Coaching"
        primaryHref="/inquire?lane=coaching"
        secondaryLabel="Take the Free Scan"
        secondaryHref="/scan"
      />

      <QuoteRibbon />
      <SiteFooter />
    </div>
  );
};

export default Reviews;
